import _styled, { css as _css, default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13, default as _styled14, default as _styled15, default as _styled16, default as _styled17, default as _styled18 } from "styled-components";
import React from "react";
import * as RadixDialog from "@radix-ui/react-dialog";
import { useRouter } from "next/router";
import { clearRouterPath } from "~/features/app/utils";
import { getThemeStyle } from "~/features/ui/get-theme-style";
import { Icon } from "~/features/ui/icon";
import { Button } from "~/features/ui/button";
import { preloadRecaptcha } from "~/features/re-captcha/utils";
import { RESERVE_GEAR_MODAL_ID } from "~/features/reserve-gear-dialog/modal";
import { ReserveGearForm, type ReserveResult } from "~/features/reserve-gear-dialog/reserve-gear-form";
import Close from "~/public/icons/close.svg";
import ArrowRight from "~/public/icons/arrow-right.svg";
type DialogState = "form" | ReserveResult;
type Props = {
  /** Slug of the product this dialog reserves — matched against `?product=`. */
  productSlug?: string | null;
  /** Rendered product image, reused in the success / error panels. */
  productImage?: React.ReactNode;
};
const MARKETING_EMAIL = "marketing@ouster.io";
function Toast({
  variant,
  children
}: {
  variant: "success" | "error";
  children: React.ReactNode;
}) {
  return <_StyledDiv role={variant === "error" ? "alert" : "status"} $_css2={[{
    "display": "flex",
    "alignItems": "center",
    "gap": "1.2rem",
    "paddingLeft": "2.4rem",
    "paddingRight": "2.4rem",
    "paddingTop": "1.6rem",
    "paddingBottom": "1.6rem",
    "fontSize": "1.4rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.014000000000000002rem",
    "fontWeight": "400",
    "@media (min-width: 1024px)": {
      "fontSize": "1.4rem",
      "lineHeight": "1.5",
      "letterSpacing": "0.014000000000000002rem",
      "fontWeight": "400"
    }
  }, variant === "success" ? {
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))",
    "--tw-text-opacity": "1",
    "color": "rgb(255 255 255 / var(--tw-text-opacity))"
  } : {
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(245 166 35 / var(--tw-bg-opacity))",
    "--tw-text-opacity": "1",
    "color": "rgb(0 7 4 / var(--tw-text-opacity))"
  }]}>
      <_StyledSpan aria-hidden $_css3={[{
      "display": "flex",
      "height": "2rem",
      "width": "2rem",
      "flexShrink": "0",
      "alignItems": "center",
      "justifyContent": "center",
      "borderRadius": "9999px",
      "borderWidth": "1px",
      "fontSize": "1.2rem",
      "lineHeight": "1",
      "letterSpacing": "0.024rem",
      "fontWeight": "400"
    }]}>
        {variant === "success" ? "✓" : "!"}
      </_StyledSpan>
      <span>{children}</span>
    </_StyledDiv>;
}
function Panel({
  toast,
  heading,
  children,
  image,
  action
}: {
  toast: React.ReactNode;
  heading: string;
  children: React.ReactNode;
  image?: React.ReactNode;
  action: React.ReactNode;
}) {
  const themeStyle = getThemeStyle({
    mode: "light"
  });
  return <>
      {toast}
      <_StyledDiv2 $_css4={[{
      "display": "flex",
      "minHeight": "0",
      "flex": "1 1 0%",
      "flexDirection": "column",
      "gap": "2.4rem",
      "overflowY": "auto",
      "paddingLeft": "2.4rem",
      "paddingRight": "2.4rem",
      "paddingTop": "3.2rem",
      "paddingBottom": "3.2rem",
      "@media (min-width: 1024px)": {
        "paddingLeft": "4rem",
        "paddingRight": "4rem"
      }
    }]}>
        <_StyledDiv3 $_css5={[{
        "display": "flex",
        "flexDirection": "column",
        "gap": "1.6rem"
      }]}>
          <_StyledH $_css6={[{
          "fontSize": "2.2rem",
          "lineHeight": "1.2",
          "fontWeight": "400",
          "@media (min-width: 1024px)": {
            "fontSize": "3.2rem",
            "lineHeight": "1.2",
            "letterSpacing": "0.032rem",
            "fontWeight": "400"
          }
        }]}>{heading}</_StyledH>
          <_StyledDiv4 $_css7={[{
          "fontSize": "1.6rem",
          "lineHeight": "1.5",
          "fontWeight": "400",
          "--tw-text-opacity": "1",
          "color": "rgb(92 92 92 / var(--tw-text-opacity))",
          "@media (min-width: 1024px)": {
            "fontSize": "1.8rem",
            "lineHeight": "1.5",
            "letterSpacing": "0.018rem",
            "fontWeight": "400"
          }
        }]}>
            {children}
          </_StyledDiv4>
        </_StyledDiv3>
        {image && <_StyledDiv5 $_css8={[{
        "overflow": "hidden",
        "borderRadius": "2px",
        "--tw-bg-opacity": "1",
        "backgroundColor": "rgb(0 7 4 / var(--tw-bg-opacity))"
      }]}>{image}</_StyledDiv5>}
      </_StyledDiv2>
      <_StyledDiv6 $_css9={[{
      "position": "sticky",
      "bottom": "0",
      "zIndex": "10",
      "borderTopWidth": "1px",
      "padding": "1.6rem"
    }, themeStyle.border, themeStyle.background]}>{action}</_StyledDiv6>
    </>;
}
export function ReserveGearDialog({
  productSlug,
  productImage
}: Props) {
  const router = useRouter();
  const themeStyle = getThemeStyle({
    mode: "light"
  });
  const activeModal = typeof router.query.modal === "string" ? router.query.modal : undefined;
  const queryProduct = typeof router.query.product === "string" ? router.query.product : undefined;
  const size = typeof router.query.size === "string" ? router.query.size : undefined;

  // Only this product's dialog opens. When no slug is provided, fall back to
  // opening on the modal id alone.
  const open = activeModal === RESERVE_GEAR_MODAL_ID && (!productSlug || queryProduct === productSlug);
  const [state, setState] = React.useState<DialogState>("form");
  const [submittedEmail, setSubmittedEmail] = React.useState("");
  React.useEffect(() => {
    if (open) preloadRecaptcha();else {
      setState("form");
      setSubmittedEmail("");
    }
  }, [open]);
  async function close() {
    const {
      modal,
      product,
      size: _size,
      ...rest
    } = router.query;
    await router.push({
      pathname: clearRouterPath(router.asPath),
      query: rest
    }, undefined, {
      shallow: true,
      scroll: false
    });
    setState("form");
    setSubmittedEmail("");
  }
  if (!open) {
    return null;
  }
  const handleResult = (result: ReserveResult, email: string) => {
    setSubmittedEmail(email);
    setState(result);
  };
  return <RadixDialog.Root open onOpenChange={next => {
    if (!next) close();
  }}>
      <RadixDialog.Portal>
        <_StyledRadixDialogOverlay $_css10={[{
        "position": "fixed",
        "inset": "0",
        "zIndex": "40",
        "backgroundColor": "rgba(0, 7, 4, 0.6)",
        "@keyframes fade-out-keyframes": {
          "from": {
            "opacity": "1"
          },
          "to": {
            "opacity": "0"
          }
        },
        "&[data-state=\"closed\"]": {
          "animation": "fade-out-keyframes 400ms ease-in-out"
        },
        "@keyframes fade-in-keyframes": {
          "from": {
            "opacity": "0"
          },
          "to": {
            "opacity": "1"
          }
        },
        "&[data-state=\"open\"]": {
          "animation": "fade-in-keyframes 400ms ease-in-out"
        }
      }, _css(["@media (prefers-reduced-motion:reduce){animation:none !important;}"])]} />
        <_StyledRadixDialogContent $_css11={[{
        "position": "fixed",
        "top": "0",
        "bottom": "0",
        "right": "0",
        "zIndex": "140",
        "display": "flex",
        "height": "100%",
        "width": "100%",
        "maxWidth": "673px",
        "flexDirection": "column",
        "@keyframes dialog-content-slide-out-keyframes": {
          "from": {
            "transform": "translateX(0)"
          },
          "to": {
            "transform": "translateX(100%)"
          }
        },
        "&[data-state=\"closed\"]": {
          "animation": "dialog-content-slide-out-keyframes 400ms ease-in-out"
        },
        "@keyframes dialog-content-slide-in-keyframes": {
          "from": {
            "transform": "translateX(100%)"
          },
          "to": {
            "transform": "translateX(0)"
          }
        },
        "&[data-state=\"open\"]": {
          "animation": "dialog-content-slide-in-keyframes 400ms ease-in-out"
        }
      }, themeStyle.background, _css(["@media (prefers-reduced-motion:reduce){animation:none !important;}"])]}>
          <_StyledHeader $_css12={[{
          "position": "sticky",
          "top": "0",
          "zIndex": "10",
          "display": "flex",
          "height": "var(--header-height)",
          "flexShrink": "0",
          "alignItems": "center",
          "borderBottomWidth": "1px"
        }, themeStyle.border, themeStyle.background]}>
            <RadixDialog.Title asChild>
              <_StyledH2>
                Reserve Gear
              </_StyledH2>
            </RadixDialog.Title>
            <RadixDialog.Close asChild>
              <_StyledButton aria-label="Close" $_css13={[{
              "marginLeft": "auto",
              "display": "flex",
              "height": "100%",
              "width": "6.4rem",
              "flexShrink": "0",
              "alignItems": "center",
              "justifyContent": "center",
              "borderLeftWidth": "1px",
              "@media (min-width: 1024px)": {
                "width": "7.2rem"
              },
              "@media (hover: hover)": {
                "&:hover > span": {
                  "--tw-rotate": "180deg",
                  "transform": "translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))"
                }
              }
            }, themeStyle.border]}>
                <_StyledIcon icon={Close} size="24" />
              </_StyledButton>
            </RadixDialog.Close>
          </_StyledHeader>

          <_StyledRadixDialogDescription>Reserve your Ouster gear.</_StyledRadixDialogDescription>

          {(state === "form" || state === "error") && <_StyledDiv7 $_css14={[{
          "display": "flex",
          "minHeight": "0",
          "flex": "1 1 0%",
          "flexDirection": "column"
        }, state === "error" && {
          "display": "none"
        }]}>
              <ReserveGearForm productSlug={productSlug} size={size} onResult={handleResult} />
            </_StyledDiv7>}

          {state === "success" && <Panel toast={<Toast variant="success">Your request has been submitted</Toast>} heading="Your order is in." image={productImage} action={<Button variant="primary" fullWidth icon={ArrowRight} onClick={close}>
                  Back to ouster.com
                </Button>}>
              We will send a confirmation and shipping notice once it goes out. Orders ship in 3–4 weeks.
            </Panel>}

          {state === "error" && <Panel toast={<Toast variant="error">Your request could not be submitted</Toast>} heading="Something went wrong." image={productImage} action={<Button variant="primary" fullWidth icon={ArrowRight} onClick={() => setState("form")}>
                  Try again
                </Button>}>
              We could not confirm your reservation. Please try again. If it keeps failing, email{" "}
              <_StyledA href={`mailto:${MARKETING_EMAIL}`} $_css15={[{
            "textDecorationLine": "underline"
          }]}>
                {MARKETING_EMAIL}
              </_StyledA>{" "}
              and we will sort it out.
            </Panel>}

          {state === "claimed" && <Panel toast={<Toast variant="error">You&rsquo;ve already claimed one</Toast>} heading="You already claimed one." image={productImage} action={<Button variant="primary" fullWidth icon={ArrowRight} onClick={close}>
                  Back to ouster.com
                </Button>}>
              We have an order on file for {submittedEmail || "your email"}. If this is a mistake, please email us at{" "}
              <_StyledA2 href={`mailto:${MARKETING_EMAIL}`} $_css16={[{
            "textDecorationLine": "underline"
          }]}>
                {MARKETING_EMAIL}
              </_StyledA2>
              .
            </Panel>}
        </_StyledRadixDialogContent>
      </RadixDialog.Portal>
    </RadixDialog.Root>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-11rzi4h-0"
})`${p => p.$_css2}`;
var _StyledSpan = _styled2("span").withConfig({
  componentId: "sc-sf-11rzi4h-1"
})`${p => p.$_css3}`;
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-11rzi4h-2"
})`${p => p.$_css4}`;
var _StyledDiv3 = _styled4("div").withConfig({
  componentId: "sc-sf-11rzi4h-3"
})`${p => p.$_css5}`;
var _StyledH = _styled5("h2").withConfig({
  componentId: "sc-sf-11rzi4h-4"
})`${p => p.$_css6}`;
var _StyledDiv4 = _styled6("div").withConfig({
  componentId: "sc-sf-11rzi4h-5"
})`${p => p.$_css7}`;
var _StyledDiv5 = _styled7("div").withConfig({
  componentId: "sc-sf-11rzi4h-6"
})`${p => p.$_css8}`;
var _StyledDiv6 = _styled8("div").withConfig({
  componentId: "sc-sf-11rzi4h-7"
})`${p => p.$_css9}`;
var _StyledRadixDialogOverlay = _styled9(RadixDialog.Overlay).withConfig({
  componentId: "sc-sf-11rzi4h-8"
})`${p => p.$_css10}`;
var _StyledRadixDialogContent = _styled10(RadixDialog.Content).withConfig({
  componentId: "sc-sf-11rzi4h-9"
})`${p => p.$_css11}`;
var _StyledHeader = _styled11("header").withConfig({
  componentId: "sc-sf-11rzi4h-10"
})`${p => p.$_css12}`;
var _StyledH2 = _styled12("h2").withConfig({
  componentId: "sc-sf-11rzi4h-11"
})({
  "paddingLeft": "1.6rem",
  "paddingRight": "1.6rem",
  "fontSize": "2rem",
  "lineHeight": "1.3",
  "letterSpacing": "0.02rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "paddingLeft": "2.4rem",
    "paddingRight": "2.4rem",
    "fontSize": "2.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.024rem",
    "fontWeight": "400"
  }
});
var _StyledButton = _styled13("button").withConfig({
  componentId: "sc-sf-11rzi4h-12"
})`${p => p.$_css13}`;
var _StyledIcon = _styled14(Icon).withConfig({
  componentId: "sc-sf-11rzi4h-13"
})({
  "transitionProperty": "transform",
  "transitionDuration": "250ms",
  "transitionTimingFunction": "ease-in-out"
});
var _StyledRadixDialogDescription = _styled15(RadixDialog.Description).withConfig({
  componentId: "sc-sf-11rzi4h-14"
})({
  "position": "absolute",
  "width": "1px",
  "height": "1px",
  "padding": "0",
  "margin": "-1px",
  "overflow": "hidden",
  "clip": "rect(0, 0, 0, 0)",
  "whiteSpace": "nowrap",
  "borderWidth": "0"
});
var _StyledDiv7 = _styled16("div").withConfig({
  componentId: "sc-sf-11rzi4h-15"
})`${p => p.$_css14}`;
var _StyledA = _styled17("a").withConfig({
  componentId: "sc-sf-11rzi4h-16"
})`${p => p.$_css15}`;
var _StyledA2 = /*#__PURE__*/_styled18("a").withConfig({
  displayName: "reserve-gear-dialog___StyledA2",
  componentId: "sc-1207hfj-0"
})(["", ""], p => p.$_css16);